import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_BASE } from './config';
import socket from '../socket/socket';
import useGroupSocket from '../socket/useGroupSocket';
import PlayerSearchDropdown from './PlayerSearchDropdown';
import useMobileViewportZoom from './useMobileViewportZoom';
import AdminSidebar from './AdminSidebar';
import AdminMessageInbox from './AdminMessageInbox';
import AdminMatchRequestInbox from './AdminMatchRequestInbox';
import { apiFetch } from '../utils/apiFetch';
import { getPlayIntensity, openAdminPlayerEditor } from '../utils/playIntensity';
import '../css/match-planner.css';

const getToken = () => localStorage.getItem('token');
const statusLabel = status => status === 'READY' ? 'พร้อมลง' : status === 'RESERVED' ? 'จองรอบถัดไป' : status;
const PlayerIntensityBadge = ({player}) => { const item=getPlayIntensity(player?.play_intensity); return <em className={`mp-intensity-badge intensity-${item.value.toLowerCase()}`}>{item.icon} {item.label}</em>; };
const AdminPlayerName = ({player}) => <button type="button" className="mp-player-name-edit" onClick={event=>{event.stopPropagation();openAdminPlayerEditor(player);}}>{player?.name||'-'}</button>;
const pairKey = (a, b) => [Number(a), Number(b)].sort((x, y) => x - y).join('-');
const PLANNER_RANKS = ['BG', 'BGM', 'NB', 'NB+', 'N-', 'N', 'N/S', 'S', 'S/P'];
const rankDistance = (left, right) => Math.abs(PLANNER_RANKS.indexOf(String(left || '').toUpperCase()) - PLANNER_RANKS.indexOf(String(right || '').toUpperCase()));

function localAlternatives(item, protectedPairs = []) {
  const players = [...(item.team1 || []), ...(item.team2 || [])];
  if (players.length !== 4) return item.alternatives?.length ? item.alternatives : [item];
  const fallbackPatterns = [
    [[players[0], players[1]], [players[2], players[3]]],
    [[players[0], players[2]], [players[1], players[3]]],
    [[players[0], players[3]], [players[1], players[2]]]
  ];
  const protectedKeys = new Set(protectedPairs.map(pair => pairKey(pair.user_a, pair.user_b)));
  const respectsProtectedPairs = (team1, team2) => [...protectedKeys].every(key => {
    const [a, b] = key.split('-').map(Number);
    const selected = players.filter(player => [a, b].includes(Number(player.user_id))).length;
    if (selected === 0) return true;
    if (selected === 1) return false;
    return [team1, team2].some(team => team.some(p => Number(p.user_id) === a) && team.some(p => Number(p.user_id) === b));
  });
  const calculatedFallbacks = fallbackPatterns
    .filter(([team1, team2]) => respectsProtectedPairs(team1, team2))
    .map(([team1, team2]) => {
    const sum1 = team1.reduce((sum, player) => sum + Number(player.point || 0), 0);
    const sum2 = team2.reduce((sum, player) => sum + Number(player.point || 0), 0);
    return { ...item, team1, team2, sum1, sum2, teamGap: Math.abs(sum1 - sum2) };
  }).sort((a, b) => a.teamGap - b.teamGap);
  const serverAlternatives = (item.alternatives || [])
    .map(alternative => ({ ...item, ...alternative }))
    .filter(alternative => respectsProtectedPairs(alternative.team1, alternative.team2));
  // Include locally calculated arrangements as well. The server may return
  // only the best partition, which previously made this button alternate
  // merely between Team 1 and Team 2 (1/2) instead of rotating all 4 players.
  const base = [...serverAlternatives, ...calculatedFallbacks];
  if (!base.length) base.push(item);
  const everySideOrder = base.flatMap(alternative => [
    alternative,
    { ...alternative, team1: alternative.team2, team2: alternative.team1, sum1: alternative.sum2, sum2: alternative.sum1 }
  ]);
  const seen = new Set();
  return everySideOrder.filter(alternative => {
    const signature = alternative.team1.map(player => player.user_id).join('-') + '|' + alternative.team2.map(player => player.user_id).join('-');
    if (seen.has(signature)) return false;
    seen.add(signature);
    return true;
  });
}

function MatchPlanner() {
  useMobileViewportZoom();

  const { groupId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState('');
  const [variantIndexes, setVariantIndexes] = useState({});
  const [notice, setNotice] = useState('');
  const [editingMatchId, setEditingMatchId] = useState('');
  const [editedMatches, setEditedMatches] = useState({});
  const [approvedMatches, setApprovedMatches] = useState({});
  const [controlOpen, setControlOpen] = useState(false);
  const [waitingPlayersOpen, setWaitingPlayersOpen] = useState(false);
  const [waitingAlertMode, setWaitingAlertMode] = useState('WAITING');
  const [waitingAlertLimit, setWaitingAlertLimit] = useState(5);
  const [waitingAlertPage, setWaitingAlertPage] = useState(0);
  const [waitingAlertPosition, setWaitingAlertPosition] = useState(() => {
    try { return JSON.parse(localStorage.getItem('match_planner_waiting_alert_position') || 'null'); }
    catch { return null; }
  });
  const [controlData, setControlData] = useState({ queue: [], playing: [] });
  const [controlLoading, setControlLoading] = useState(false);
  const [controlBusy, setControlBusy] = useState('');
  const [pairSaving, setPairSaving] = useState(false);
  const [requestBusy, setRequestBusy] = useState('');
  const [primaryPlayerId, setPrimaryPlayerId] = useState('');
  const [selectedRanks, setSelectedRanks] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [minPoint, setMinPoint] = useState('');
  const [maxPoint, setMaxPoint] = useState('');
  const [holdOpen, setHoldOpen] = useState(false);
  const [holdSearch, setHoldSearch] = useState('');
  const [holdBusy, setHoldBusy] = useState('');
  const [manualOpen, setManualOpen] = useState(false);
  const [manualPlayers, setManualPlayers] = useState([null, null, null, null]);
  const [manualBusy, setManualBusy] = useState('');
  const [manualSearch, setManualSearch] = useState('');
  const [manualOrder, setManualOrder] = useState('NEARBY');
  const [manualHistory, setManualHistory] = useState({});
  const [manualRelationCheck, setManualRelationCheck] = useState({ loading: false, teammateDetails: [], opponentDetails: [], error: '' });
  const [manualPartnerCheck, setManualPartnerCheck] = useState({ anchorUserId: null, loading: false, relations: {} });
  const [autoQueueEnabled, setAutoQueueEnabled] = useState(() => localStorage.getItem(`match_planner_auto_queue_${groupId}`) === 'true');
  const [autoQueueBusy, setAutoQueueBusy] = useState(false);
  const refreshRoundRef = useRef(0);
  const loadInFlightRef = useRef(false);
  const queuedLoadRef = useRef(null);
  const controlLoadInFlightRef = useRef(false);
  const controlLoadQueuedRef = useRef(false);
  const autoQueueBusyRef = useRef(false);
  const waitingAlertDragRef = useRef(null);
  const waitingAlertMovedRef = useRef(false);
  const [settings, setSettings] = useState({ playerGap: 150, teamGap: 50, playingAfterMinutes: 2, reservationMinutes: 15 });

  const api = useCallback(async (path, options = {}) => {
    const response = await apiFetch(`${API_BASE}${path}`, { ...options, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}`, ...(options.headers || {}) } });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      const requestError = new Error(result.message || `HTTP ${response.status}`);
      Object.assign(requestError, result, { status: response.status });
      throw requestError;
    }
    return result;
  }, []);

  const loadControlData = useCallback(async (quiet = false) => {
    if (controlLoadInFlightRef.current) {
      controlLoadQueuedRef.current = true;
      return;
    }
    controlLoadInFlightRef.current = true;
    if (!quiet) setControlLoading(true);
    try {
      const [queueResult, allMatches] = await Promise.all([
        api(`/api/game-match/queue/${Number(groupId)}`),
        api(`/api/game-match/group/${Number(groupId)}`)
      ]);
      setControlData({
        queue: Array.isArray(queueResult?.matches) ? queueResult.matches : [],
        playing: (Array.isArray(allMatches) ? allMatches : []).filter(match => String(match.status).toLowerCase() === 'playing')
      });
    } catch (controlError) {
      setError(controlError.message);
    } finally {
      setControlLoading(false);
      controlLoadInFlightRef.current = false;
      if (controlLoadQueuedRef.current) {
        controlLoadQueuedRef.current = false;
        window.setTimeout(() => loadControlData(true), 0);
      }
    }
  }, [api, groupId]);

  const load = useCallback(async (quiet = false, round = refreshRoundRef.current) => {
    if (loadInFlightRef.current) {
      queuedLoadRef.current = { quiet: true, round };
      return;
    }
    loadInFlightRef.current = true;
    if (!quiet) setLoading(true);
    try {
      const [result, lockedPairs] = await Promise.all([
        api(`/api/match-planner/${Number(groupId)}?round=${Number(round) || 0}${primaryPlayerId ? `&priorityUserId=${Number(primaryPlayerId)}` : ''}${selectedRanks.length ? `&ranks=${encodeURIComponent(selectedRanks.join(','))}` : ''}${statusFilter ? `&status=${statusFilter}` : ''}${minPoint !== '' ? `&minPoint=${encodeURIComponent(minPoint)}` : ''}${maxPoint !== '' ? `&maxPoint=${encodeURIComponent(maxPoint)}` : ''}`),
        api(`/api/game-match/locked-pairs/${Number(groupId)}`).catch(() => [])
      ]);
      const existingKeys = new Set((result.waitingPairs || []).map(pair => pairKey(pair.user_a, pair.user_b)));
      const recommendationSignatures = new Set();
      const uniqueRecommendations = (result.recommendations || []).filter(match => {
        const signature = [...(match.team1 || []), ...(match.team2 || [])]
          .map(player => Number(player.user_id))
          .sort((left, right) => left - right)
          .join('-');
        if (!signature || recommendationSignatures.has(signature)) return false;
        recommendationSignatures.add(signature);
        return true;
      });
      const automaticLockedPairs = (Array.isArray(lockedPairs) ? lockedPairs : []).filter(pair => !existingKeys.has(pairKey(pair.user_a, pair.user_b))).map(pair => ({
        id: `locked-${pair.user_a}-${pair.user_b}`,
        group_id: Number(groupId), user_a: pair.user_a, user_b: pair.user_b,
        user_a_name: pair.name_a, user_b_name: pair.name_b,
        status: 'LOCKED', score_gap: '—', pair_source: 'LOCKED'
      }));
      setData({ ...result, recommendations: uniqueRecommendations, lockedPairs: Array.isArray(lockedPairs) ? lockedPairs : [], waitingPairs: [...automaticLockedPairs, ...(result.waitingPairs || [])] }); setError('');
      setSettings({ playerGap: result.settings.player_gap, teamGap: result.settings.team_gap, playingAfterMinutes: result.settings.playing_after_minutes, reservationMinutes: result.settings.reservation_minutes });
    } catch (loadError) { setError(loadError.message); }
    finally {
      setLoading(false);
      loadInFlightRef.current = false;
      const queuedLoad = queuedLoadRef.current;
      queuedLoadRef.current = null;
      if (queuedLoad) {
        window.setTimeout(() => load(queuedLoad.quiet, queuedLoad.round), 0);
      }
    }
  }, [api, groupId, primaryPlayerId, selectedRanks, statusFilter, minPoint, maxPoint]);

  useGroupSocket(groupId);

  const chooseStatusFilter = status => {
    setStatusFilter(current => current === status ? '' : status);
    setPrimaryPlayerId('');
    setVariantIndexes({});
    setEditedMatches({});
    setApprovedMatches({});
    setError('');
  };

  const togglePlannerRank = rank => {
    setSelectedRanks(current => {
      if (current.includes(rank)) return current.filter(item => item !== rank);
      if (current.length >= 3) {
        setError('เลือกระดับมือได้สูงสุด 3 ประเภท');
        return current;
      }
      setError('');
      return [...current, rank];
    });
    setVariantIndexes({});
    setEditedMatches({});
  };

  const refreshNextRound = () => {
    // Move by the number of cards currently shown so the next response starts
    // from a completely new recommendation window instead of replacing only
    // one card while keeping the other seven.
    refreshRoundRef.current += Math.max(1, Number(data?.recommendations?.length || 0));
    setVariantIndexes({});
    setEditedMatches({});
    setEditingMatchId('');
    setApprovedMatches({});
    setSelected('');
    setError('');
    setNotice('');
    load(false, refreshRoundRef.current);
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    localStorage.setItem(`match_planner_auto_queue_${groupId}`, String(autoQueueEnabled));
  }, [autoQueueEnabled, groupId]);
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('manual') === '1') {
      setManualOpen(true);
    }
  }, []);
  useEffect(() => {
    if (window.location.hash !== '#lock-pair' || !data) return;
    window.setTimeout(() => document.getElementById('lock-pair')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
  }, [data]);
  useEffect(() => {
    let refreshTimerId;
    const refresh = event => {
      if (event?.groupId && Number(event.groupId) !== Number(groupId)) return;
      window.clearTimeout(refreshTimerId);
      refreshTimerId = window.setTimeout(() => load(true), 350);
    };
    ['match-planner-updated','queue-updated','player-status-updated','match-started','match-ended','member-updated'].forEach(name => socket.on(name, refresh));
    return () => {
      window.clearTimeout(refreshTimerId);
      ['match-planner-updated','queue-updated','player-status-updated','match-started','match-ended','member-updated'].forEach(name => socket.off(name, refresh));
    };
  }, [groupId, load]);
  useEffect(() => {
    if (!controlOpen) return undefined;
    loadControlData();
    const refreshControl = event => {
      if (!event?.groupId || Number(event.groupId) === Number(groupId)) loadControlData(true);
    };
    ['queue-updated','player-status-updated','match-started','match-ended','member-updated'].forEach(name => socket.on(name, refreshControl));
    return () => ['queue-updated','player-status-updated','match-started','match-ended','member-updated'].forEach(name => socket.off(name, refreshControl));
  }, [controlOpen, groupId, loadControlData]);

  const startControlMatch = async match => {
    setControlBusy(`start-${match.matchId}`);
    try {
      await api('/api/game-match/start', { method: 'POST', body: JSON.stringify({ matchId: match.matchId, groupId: Number(groupId), approvePlaying: false }) });
      setNotice(`✅ เริ่ม Match #${match.groupMatchNo || match.matchId} แล้ว`);
      await Promise.all([loadControlData(true), load(true)]);
    } catch (startError) {
      const message = String(startError.message || 'เริ่ม Match ไม่สำเร็จ');
      if (/approve|active|play|สถานะ/i.test(message) && window.confirm(`${message}\n\nต้องการ Approve และเริ่ม Match หรือไม่?`)) {
        try {
          await api('/api/game-match/start', { method: 'POST', body: JSON.stringify({ matchId: match.matchId, groupId: Number(groupId), approvePlaying: true }) });
          setNotice(`✅ Approve และเริ่ม Match #${match.groupMatchNo || match.matchId} แล้ว`);
          await Promise.all([loadControlData(true), load(true)]);
        } catch (approvedError) { setError(approvedError.message); }
      } else setError(message);
    } finally { setControlBusy(''); }
  };

  const endControlMatch = async (match, winnerTeam) => {
    const label = winnerTeam === 'TEAM1' ? 'T1 ชนะ' : winnerTeam === 'TEAM2' ? 'T2 ชนะ' : 'เสมอ';
    if (!window.confirm(`ยืนยันจบ Match #${match.groupMatchNo || match.matchId} เป็น ${label}?`)) return;
    setControlBusy(`end-${match.matchId}`);
    try {
      await api('/api/game-match/end', { method: 'POST', body: JSON.stringify({ matchId: match.matchId, winnerTeam }) });
      setNotice(`✅ จบ Match #${match.groupMatchNo || match.matchId}: ${label}`);
      await Promise.all([loadControlData(true), load(true)]);
    } catch (endError) { setError(endError.message); }
    finally { setControlBusy(''); }
  };

  const compactPlayers = match => {
    if (Array.isArray(match.players)) return [1,2].map(team => match.players.filter(player => Number(player.team) === team));
    return [match.team1 || [], match.team2 || []];
  };

  const waitingPlayers = [...(data?.players || [])].sort((a, b) =>
    Number(b.wait_minutes || 0) - Number(a.wait_minutes || 0) ||
    Number(a.total_games || 0) - Number(b.total_games || 0) ||
    String(a.name || '').localeCompare(String(b.name || ''), 'th')
  );
  const waitingActivePlayers = waitingPlayers.filter(player => String(player.status || '').toUpperCase() === 'ACTIVE');
  const lowGamePlayers = [...waitingActivePlayers].sort((a, b) =>
    Number(a.total_games || 0) - Number(b.total_games || 0) ||
    Number(b.wait_minutes || 0) - Number(a.wait_minutes || 0)
  );
  const waitingAlertPlayers = waitingAlertMode === 'LOW_GAMES' ? lowGamePlayers : waitingActivePlayers;
  const waitingAlertPageCount = Math.max(1, Math.ceil(waitingAlertPlayers.length / waitingAlertLimit));
  const visibleWaitingAlertPlayers = waitingAlertPlayers.slice(waitingAlertPage * waitingAlertLimit, (waitingAlertPage + 1) * waitingAlertLimit);
  const eligiblePlayerIds = new Set((data?.eligible || []).map(player => Number(player.user_id)));
  const eligibleWaitingPlayers = waitingPlayers
    .filter(player => eligiblePlayerIds.has(Number(player.user_id)))
    .sort((a, b) => {
      const aWaiting = String(a.status || '').toUpperCase() === 'ACTIVE' ? 0 : 1;
      const bWaiting = String(b.status || '').toUpperCase() === 'ACTIVE' ? 0 : 1;
      return aWaiting - bWaiting
        || Number(b.wait_minutes || 0) - Number(a.wait_minutes || 0)
        || Number(a.total_games || 0) - Number(b.total_games || 0)
        || String(a.name || '').localeCompare(String(b.name || ''), 'th');
    });
  const choosePrimaryPlayer = player => {
    if (!eligiblePlayerIds.has(Number(player.user_id))) {
      setError(`${player.name} ยังเลือกเป็นผู้เล่นหลักไม่ได้ เพราะติด Queue หรือสถานะยังไม่พร้อม`);
      return;
    }
    setPrimaryPlayerId(String(player.user_id));
    setWaitingPlayersOpen(false);
    setVariantIndexes({}); setEditedMatches({}); setApprovedMatches({}); setError('');
  };

  const saveSettings = async () => {
    setSaving(true);
    try { await api(`/api/match-planner/${groupId}/settings`, { method: 'PUT', body: JSON.stringify(settings) }); await load(true); }
    catch (saveError) { setError(saveError.message); }
    finally { setSaving(false); }
  };

  const decideRequest = async (request, decision) => {
    const action = decision === 'APPROVE' ? 'Approve' : 'ปฏิเสธ';
    if (!window.confirm(`ยืนยัน ${action} คำขอของ ${request.requester_name}?`)) return;
    setRequestBusy(`${request.id}-${decision}`); setError('');
    try {
      await api(`/api/match-planner/${groupId}/requests/${request.id}/decision`, { method: 'POST', body: JSON.stringify({ decision }) });
      setNotice(decision === 'APPROVE' ? `✅ Approve คำขอ ${request.request_mode} คนแล้ว` : 'ยกเลิกคำขอแล้ว');
      await load(true);
    } catch (requestError) { setError(requestError.message); }
    finally { setRequestBusy(''); }
  };

  const createQueue = async suggestion => {
    const wasEdited = Boolean(editedMatches[suggestion.id]);
    const repeatApproved = Boolean(approvedMatches[suggestion.id]);
    if (suggestion.requiresApproval && !repeatApproved) {
      setError('Match นี้มีผู้เล่นคู่กันหรือตรงข้ามกันครบ 2 รอบแล้ว กรุณากด Approve ก่อนสร้าง Queue');
      return;
    }
    const selectedPlayers = [...suggestion.team1, ...suggestion.team2];
    const queuePlayers = selectedPlayers.filter(player => player.status === 'QUEUE' || player.queued_match_id);
    const repeatWarning = suggestion.requiresApproval
      ? `\n\n✅ Admin Approve ประวัติซ้ำแล้ว:\n- ${(suggestion.repeatViolations || []).join('\n- ')}`
      : '';
    const queueWarning = queuePlayers.length
      ? `\n\n⚠️ เลือกผ่าน Edit และอยู่ใน Queue อื่น: ${queuePlayers.map(player => player.name).join(', ')}\nกดตกลงเพื่อ Approve ให้ผู้เล่นอยู่มากกว่า 1 Queue`
      : '';
    const warning = `${suggestion.relaxed ? `ชุดนี้ผ่อนปรนกติกา: คะแนนผู้เล่นห่าง ${suggestion.playerGap} และคะแนนรวมทีมห่าง ${suggestion.teamGap}` : 'Match นี้ผ่านเกณฑ์ปกติ'}${repeatWarning}${queueWarning}\n\n${suggestion.requiresApproval || queuePlayers.length ? 'กดตกลงเพื่อ Approve และสร้าง Queue' : 'ยืนยันสร้าง Queue?'}`;
    if (!window.confirm(warning)) return;
    setSelected(suggestion.id);
    try {
      const payload = { team1: suggestion.team1.map(p => p.user_id), team2: suggestion.team2.map(p => p.user_id), approveRepeat: repeatApproved, approveQueueConflict: queuePlayers.length > 0 };
      let result;
      try {
        result = await api(`/api/match-planner/${groupId}/queue`, { method: 'POST', body: JSON.stringify(payload) });
      } catch (queueError) {
        if (!queueError.requiresQueueApproval || !window.confirm(`${queueError.message}\n\nยืนยัน Approve และสร้าง Queue หรือไม่?`)) throw queueError;
        result = await api(`/api/match-planner/${groupId}/queue`, { method: 'POST', body: JSON.stringify({ ...payload, approveQueueConflict: true }) });
      }
      setNotice(`✅ สร้าง Match Queue #${result.groupMatchNo} สำเร็จ`);
      if (wasEdited) {
        setEditedMatches(previous => {
          const next = { ...previous };
          delete next[suggestion.id];
          return next;
        });
        setEditingMatchId('');
        setApprovedMatches(previous => {
          const next = { ...previous };
          delete next[suggestion.id];
          return next;
        });
        setVariantIndexes(previous => {
          const next = { ...previous };
          delete next[suggestion.id];
          return next;
        });
        refreshRoundRef.current += 1;
        await load(true, refreshRoundRef.current);
      } else {
        await load(true);
      }
    } catch (queueError) { setError(queueError.message); }
    finally { setSelected(''); }
  };

  useEffect(() => {
    if (!autoQueueEnabled || loading || autoQueueBusyRef.current || selected || editingMatchId) return;
    const suggestion = (data?.recommendations || []).find(item => {
      const players = [...(item.team1 || []), ...(item.team2 || [])];
      return item.status === 'READY'
        && Number(item.playerGap || 0) <= Number(settings.playerGap || 0)
        && Number(item.teamGap || 0) <= Number(settings.teamGap || 0)
        && !item.requiresApproval
        && !item.historyChecking && players.length === 4
        && players.every(player => player.status === 'ACTIVE' && !player.queued_match_id);
    });
    if (!suggestion) return;

    const timerId = window.setTimeout(async () => {
      autoQueueBusyRef.current = true;
      setAutoQueueBusy(true);
      try {
        const result = await api(`/api/match-planner/${groupId}/queue`, {
          method: 'POST',
          body: JSON.stringify({
            team1: suggestion.team1.map(player => player.user_id),
            team2: suggestion.team2.map(player => player.user_id),
            approveRepeat: false,
            approveQueueConflict: false
          })
        });
        setNotice(`🤖 Auto Queue สร้าง Match #${result.groupMatchNo} สำเร็จ`);
        setError('');
        await load(true);
      } catch (autoError) {
        setAutoQueueEnabled(false);
        setError(`Auto Queue ถูกปิด: ${autoError.message}`);
      } finally {
        autoQueueBusyRef.current = false;
        setAutoQueueBusy(false);
      }
    }, 900);
    return () => window.clearTimeout(timerId);
  }, [api, autoQueueEnabled, data?.recommendations, editingMatchId, groupId, load, loading, selected, settings.playerGap, settings.teamGap]);

  // Waiting Pair is an explicit Admin action, so its search must not be limited
  // by the smaller calculation window used for automatic recommendations.
  const selectable = (data?.players || []).filter(player => player.status !== 'HOLD');
  const [pairA, setPairA] = useState(null);
  const [pairB, setPairB] = useState(null);
  const createPair = async (suggestedA = pairA, suggestedB = pairB) => {
    const userA = typeof suggestedA === 'object' ? suggestedA?.user_id : suggestedA;
    const userB = typeof suggestedB === 'object' ? suggestedB?.user_id : suggestedB;
    const occupied = (data?.waitingPairs || []).find(pair => [Number(pair.user_a),Number(pair.user_b)].some(id => id===Number(userA)||id===Number(userB)));
    if (occupied) { setError(`สร้างคู่ไม่ได้: ${occupied.user_a_name} + ${occupied.user_b_name} ถูกล็อกเป็นคู่อยู่แล้ว`); return; }
    if (pairSaving) return;
    setPairSaving(true);
    try { await api(`/api/match-planner/${groupId}/pairs`, { method: 'POST', body: JSON.stringify({ userA, userB }) }); setPairA(null); setPairB(null); await load(true); }
    catch (pairError) { setError(pairError.message); }
    finally { setPairSaving(false); }
  };

  const updateHoldStatus = async (player, nextStatus) => {
    const busyKey = `${player.user_id}-${nextStatus}`;
    setHoldBusy(busyKey);
    try {
      await api(`/api/users/${Number(player.user_id)}/group/${Number(groupId)}/status`, {
        method: 'PUT', body: JSON.stringify({ status: nextStatus })
      });
      setNotice(`✅ เปลี่ยน ${player.name} เป็น ${nextStatus} แล้ว`);
      await load(true);
    } catch (statusError) { setError(statusError.message); }
    finally { setHoldBusy(''); }
  };

  const moveWaitingAlert = event => {
    const drag = waitingAlertDragRef.current;
    if (!drag) return;
    if (Math.abs(event.clientX-drag.pointerX)+Math.abs(event.clientY-drag.pointerY)>5) waitingAlertMovedRef.current=true;
    const maxX = Math.max(8, window.innerWidth - drag.width - 8);
    const maxY = Math.max(8, window.innerHeight - drag.height - 8);
    setWaitingAlertPosition({
      x: Math.min(maxX, Math.max(8, drag.left + event.clientX - drag.pointerX)),
      y: Math.min(maxY, Math.max(8, drag.top + event.clientY - drag.pointerY))
    });
  };
  const finishWaitingAlertDrag = event => {
    if (!waitingAlertDragRef.current) return;
    waitingAlertDragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    setWaitingAlertPosition(current => {
      try { localStorage.setItem('match_planner_waiting_alert_position', JSON.stringify(current)); }
      catch { /* browser may block storage */ }
      return current;
    });
  };

  const createManualMatch = async startImmediately => {
    const picked = manualPlayers.filter(Boolean);
    if (picked.length !== 4 || new Set(picked.map(player => Number(player.user_id))).size !== 4) {
      setError('กรุณาเลือกผู้เล่นไม่ซ้ำกันให้ครบ 4 คน');
      return;
    }
    setManualBusy(startImmediately ? 'start' : 'queue');
    const payload = {
      team1: picked.slice(0, 2).map(player => Number(player.user_id)),
      team2: picked.slice(2, 4).map(player => Number(player.user_id))
    };
    try {
      let created;
      try {
        created = await api(`/api/match-planner/${groupId}/queue`, { method: 'POST', body: JSON.stringify(payload) });
      } catch (queueError) {
        const canApprove = queueError.code === 'REPEAT_APPROVAL_REQUIRED' || queueError.requiresQueueApproval;
        if (!canApprove || !window.confirm(`${queueError.message}\n\nต้องการ Approve และดำเนินการต่อหรือไม่?`)) throw queueError;
        created = await api(`/api/match-planner/${groupId}/queue`, {
          method: 'POST', body: JSON.stringify({ ...payload, approveRepeat: true, approveQueueConflict: true })
        });
      }
      if (startImmediately) {
        try {
          await api('/api/game-match/start', {
            method: 'POST', body: JSON.stringify({ matchId: created.matchId, groupId: Number(groupId), approvePlaying: true })
          });
        } catch (startError) {
          setNotice(`⚠️ สร้าง Match #${created.groupMatchNo || created.matchId} เข้า Queue แล้ว แต่ยังเริ่มไม่ได้: ${startError.message}`);
          setManualPlayers([null, null, null, null]);
          setManualOpen(false);
          await load(true);
          return;
        }
      }
      setNotice(startImmediately ? `✅ สร้างและเริ่ม Match #${created.groupMatchNo || created.matchId} แล้ว` : `✅ ส่ง Match #${created.groupMatchNo || created.matchId} เข้า Queue แล้ว`);
      setManualPlayers([null, null, null, null]);
      setManualOpen(false);
      await load(true);
    } catch (manualError) { setError(manualError.message); }
    finally { setManualBusy(''); }
  };

  const toggleManualPlayer = async player => {
    const playerId = Number(player.user_id);
    setManualPlayers(current => {
      const existingIndex = current.findIndex(item => Number(item?.user_id) === playerId);
      if (existingIndex >= 0) return current.map((item, index) => index === existingIndex ? null : item);
      const emptyIndex = current.findIndex(item => !item);
      if (emptyIndex < 0) return current;
      return current.map((item, index) => index === emptyIndex ? player : item);
    });
    if (!manualHistory[playerId]) {
      setManualHistory(current => ({ ...current, [playerId]: { loading: true, matches: [] } }));
      try {
        const result = await api(`/api/match-planner/${groupId}/player-history/${playerId}`);
        setManualHistory(current => ({ ...current, [playerId]: { loading: false, matches: result.matches || [] } }));
      } catch (historyError) {
        setManualHistory(current => ({ ...current, [playerId]: { loading: false, matches: [], error: historyError.message || 'โหลดประวัติไม่สำเร็จ' } }));
      }
    }
  };

  useEffect(() => {
    const picked = manualPlayers.filter(Boolean);
    if (picked.length !== 4) {
      setManualRelationCheck({ loading: false, teammateDetails: [], opponentDetails: [], error: '' });
      return undefined;
    }
    let cancelled = false;
    setManualRelationCheck(current => ({ ...current, loading: true, error: '' }));
    api(`/api/match-planner/${groupId}/check-history`, {
      method: 'POST',
      body: JSON.stringify({
        team1: picked.slice(0, 2).map(player => Number(player.user_id)),
        team2: picked.slice(2, 4).map(player => Number(player.user_id))
      })
    }).then(result => {
      if (!cancelled) setManualRelationCheck({ loading: false, teammateDetails: result.teammateDetails || [], opponentDetails: result.opponentDetails || [], error: '' });
    }).catch(relationError => {
      if (!cancelled) setManualRelationCheck({ loading: false, teammateDetails: [], opponentDetails: [], error: relationError.message || 'ตรวจประวัติไม่สำเร็จ' });
    });
    return () => { cancelled = true; };
  }, [api, groupId, manualPlayers]);

  useEffect(() => {
    const nextSlot = manualPlayers.findIndex(player => !player);
    const anchor = nextSlot === 1 ? manualPlayers[0] : nextSlot === 3 ? manualPlayers[2] : null;
    if (!anchor) {
      setManualPartnerCheck({ anchorUserId: null, loading: false, relations: {} });
      return undefined;
    }
    let cancelled = false;
    const anchorUserId = Number(anchor.user_id);
    setManualPartnerCheck({ anchorUserId, loading: true, relations: {} });
    api(`/api/match-planner/${groupId}/partner-history/${anchorUserId}`).then(result => {
      if (cancelled) return;
      const relations = Object.fromEntries((result.relations || []).map(item => [Number(item.userId), item]));
      setManualPartnerCheck({ anchorUserId, loading: false, relations });
    }).catch(() => {
      if (!cancelled) setManualPartnerCheck({ anchorUserId, loading: false, relations: {} });
    });
    return () => { cancelled = true; };
  }, [api, groupId, manualPlayers]);

  const protectedPairFor = player => (data?.waitingPairs || []).find(pair =>
    [Number(pair.user_a), Number(pair.user_b)].includes(Number(player?.user_id))
  );
  const editablePlayers = (data?.players || []).filter(player => player.status !== 'HOLD' && !protectedPairFor(player));
  const manualAnchor = manualPlayers.find(Boolean);
  const manualSelectedIds = new Set(manualPlayers.filter(Boolean).map(player => Number(player.user_id)));
  const nextManualSlot = manualPlayers.findIndex(player => !player);
  const partnerCheckAnchor = nextManualSlot === 1 ? manualPlayers[0] : nextManualSlot === 3 ? manualPlayers[2] : null;
  const manualCandidates = selectable
    .filter(player => !manualSearch.trim() || String(player.name || '').toLowerCase().includes(manualSearch.trim().toLowerCase()))
    .sort((left, right) => {
      if (manualOrder === 'WAIT_LONG') {
        return Number(right.wait_minutes || 0) - Number(left.wait_minutes || 0)
          || Number(left.total_games || 0) - Number(right.total_games || 0)
          || Number(right.status === 'ACTIVE') - Number(left.status === 'ACTIVE');
      }
      if (manualOrder === 'LOW_GAMES') {
        return Number(left.total_games || 0) - Number(right.total_games || 0)
          || Number(right.wait_minutes || 0) - Number(left.wait_minutes || 0)
          || Number(right.status === 'ACTIVE') - Number(left.status === 'ACTIVE');
      }
      if (!manualAnchor) return Number(right.status === 'ACTIVE') - Number(left.status === 'ACTIVE') || Number(left.wait_minutes || 0) - Number(right.wait_minutes || 0);
      return rankDistance(left.level_rank, manualAnchor.level_rank) - rankDistance(right.level_rank, manualAnchor.level_rank)
        || Math.abs(Number(left.point || 0) - Number(manualAnchor.point || 0)) - Math.abs(Number(right.point || 0) - Number(manualAnchor.point || 0));
    });
  const manualRelationRows = [
    ...manualRelationCheck.teammateDetails.map(item => ({ ...item, relation: 'TEAMMATE' })),
    ...manualRelationCheck.opponentDetails.map(item => ({ ...item, relation: 'OPPONENT' }))
  ];
  const recheckMatchHistory = (matchId, match) => {
    const checkingMatch = { ...match, historyChecking: true };
    const team1Ids = checkingMatch.team1.map(player => Number(player.user_id));
    const team2Ids = checkingMatch.team2.map(player => Number(player.user_id));
    const selectionKey = [...team1Ids, ...team2Ids].join('-');
    setEditedMatches(previous => ({ ...previous, [matchId]: checkingMatch }));
    setApprovedMatches(previous => { const next = { ...previous }; delete next[matchId]; return next; });
    api('/api/match-planner/' + groupId + '/check-history', {
      method: 'POST', body: JSON.stringify({ team1: team1Ids, team2: team2Ids })
    }).then(historyResult => setEditedMatches(previous => {
      const latest = previous[matchId];
      if (!latest || [...latest.team1, ...latest.team2].map(player => Number(player.user_id)).join('-') !== selectionKey) return previous;
      return { ...previous, [matchId]: { ...latest, ...historyResult, historyChecking: false } };
    })).catch(historyError => {
      setEditedMatches(previous => {
        const latest = previous[matchId];
        if (!latest || [...latest.team1, ...latest.team2].map(player => Number(player.user_id)).join('-') !== selectionKey) return previous;
        return { ...previous, [matchId]: { ...latest, historyChecking: false } };
      });
      setError('ตรวจประวัติคู่ใหม่ไม่สำเร็จ: ' + historyError.message);
    });
  };
  const updateMatchPlayer = (matchId, current, teamKey, playerIndex, replacement) => {
    const next = editedMatches[matchId]
      ? { ...editedMatches[matchId], team1: [...editedMatches[matchId].team1], team2: [...editedMatches[matchId].team2] }
      : { ...current, team1: [...current.team1], team2: [...current.team2] };
    next[teamKey][playerIndex] = replacement;
    next.sum1 = next.team1.reduce((sum, player) => sum + Number(player.point || 0), 0);
    next.sum2 = next.team2.reduce((sum, player) => sum + Number(player.point || 0), 0);
    next.teamGap = Math.abs(next.sum1 - next.sum2);
    const points = [...next.team1, ...next.team2].map(player => Number(player.point || 0));
    next.playerGap = Math.max(...points) - Math.min(...points);
    next.relaxed = next.playerGap > Number(settings.playerGap) || next.teamGap > Number(settings.teamGap);
    next.historyChecking = true;
    setEditedMatches(previous => ({ ...previous, [matchId]: next }));
    setApprovedMatches(previous => {
      const nextApprovals = { ...previous };
      delete nextApprovals[matchId];
      return nextApprovals;
    });
    const team1Ids = next.team1.map(player => Number(player.user_id));
    const team2Ids = next.team2.map(player => Number(player.user_id));
    const selectionKey = [...team1Ids, ...team2Ids].join('-');
    api(`/api/match-planner/${groupId}/check-history`, {
      method: 'POST',
      body: JSON.stringify({ team1: team1Ids, team2: team2Ids })
    }).then(historyResult => {
      setEditedMatches(previous => {
        const latest = previous[matchId];
        if (!latest || [...latest.team1, ...latest.team2].map(player => Number(player.user_id)).join('-') !== selectionKey) return previous;
        return { ...previous, [matchId]: { ...latest, ...historyResult, historyChecking: false } };
      });
    }).catch(historyError => {
      setEditedMatches(previous => {
        const latest = previous[matchId];
        if (!latest || [...latest.team1, ...latest.team2].map(player => Number(player.user_id)).join('-') !== selectionKey) return previous;
        return { ...previous, [matchId]: { ...latest, historyChecking: false } };
      });
      setError(`ตรวจประวัติคู่ใหม่ไม่สำเร็จ: ${historyError.message}`);
    });
  };

  const renderPlayer = player => <div className={`mp-player ${player.status === 'PLAY' ? 'is-play' : ''}`} key={player.user_id}>
    <div className="mp-player-main"><div><strong><AdminPlayerName player={player}/></strong><span>{player.level_rank} <PlayerIntensityBadge player={player}/></span></div><small>{player.status}{player.status === 'PLAY' ? ` ${player.playing_minutes} นาที` : ` · รอ ${player.wait_minutes || 0} นาที`}</small></div>
  </div>;

  return <div className="mp-page admin-shell-with-sidebar">
    <AdminSidebar active="planner" groupId={groupId} onRefresh={refreshNextRound} />
    <AdminMessageInbox groupId={Number(groupId)} />
    <AdminMatchRequestInbox groupId={Number(groupId)} />
    {!waitingPlayersOpen&&<button type="button" className={`rmp-waiting-alert-trigger${waitingActivePlayers.length===0?' is-empty':''}`} style={waitingAlertPosition?{left:waitingAlertPosition.x,top:waitingAlertPosition.y,right:'auto'}:undefined} onPointerDown={event=>{const rect=event.currentTarget.getBoundingClientRect();waitingAlertMovedRef.current=false;waitingAlertDragRef.current={pointerX:event.clientX,pointerY:event.clientY,left:rect.left,top:rect.top,width:rect.width,height:rect.height};event.currentTarget.setPointerCapture?.(event.pointerId);}} onPointerMove={moveWaitingAlert} onPointerUp={finishWaitingAlertDrag} onPointerCancel={finishWaitingAlertDrag} onClick={()=>{if(waitingAlertMovedRef.current){waitingAlertMovedRef.current=false;return;}setWaitingPlayersOpen(true);}}><i aria-hidden="true">⚠️</i><span>แจ้งเตือนผู้เล่น</span>{waitingActivePlayers.length>0&&<b>{waitingActivePlayers.length}</b>}</button>}
    <header className="mp-header"><div><button onClick={() => navigate('/dashboard')}>← Dashboard</button><p>MATCH PLANNER</p><h1>จัดแมตช์ให้ทัน และสูสีที่สุด</h1><span>จับคู่ 2 คนก่อน · จองผู้เล่นที่ใกล้จบ · ส่งเข้าคิวเมื่อพร้อม</span></div><div className="mp-header-actions"><button className={`mp-auto-queue-toggle ${autoQueueEnabled?'is-on':''}`} onClick={()=>setAutoQueueEnabled(enabled=>!enabled)}>{autoQueueBusy?'⏳ กำลังเข้าคิว':autoQueueEnabled?'🤖 จัดคิวอัตโนมัติ: เปิด':'🤖 จัดคิวอัตโนมัติ: ปิด'}</button><button className="mp-control-open" onClick={()=>setManualOpen(true)}>👥 เลือก 4 คน</button><button className="mp-control-open" onClick={()=>setControlOpen(true)}>🏸 คิว / จบเกม</button><button className="mp-control-open" onClick={()=>setHoldOpen(true)}>⏸ พักผู้เล่น</button><button className="mp-control-open" onClick={()=>window.open(`/game-match-control/${groupId}`,'_blank','noopener,noreferrer')}>🖥 ควบคุมสนาม</button><button className="mp-refresh" onClick={refreshNextRound}>↻ คิวแนะนำถัดไป</button></div></header>
    {error && <div className="mp-error">⚠️ {error}</div>}
    {notice && <div className="mp-notice">{notice}<button onClick={()=>setNotice('')}>×</button></div>}
    <section className="mp-settings"><div><h2>⚙️ เกณฑ์การจัด</h2><p>ขยายค่าได้เมื่อต้องการเน้นให้ผู้เล่นได้ลงสนาม</p></div>
      <label>คะแนนผู้เล่นห่างไม่เกิน<input type="number" value={settings.playerGap} onChange={e => setSettings(s => ({...s, playerGap:e.target.value}))}/></label>
      <label>คะแนนรวมทีมห่างไม่เกิน<input type="number" value={settings.teamGap} onChange={e => setSettings(s => ({...s, teamGap:e.target.value}))}/></label>
      <label>คะแนนต่ำสุด<input type="number" min="0" placeholder="ทั้งหมด" value={minPoint} onChange={e => setMinPoint(e.target.value)}/></label>
      <label>คะแนนสูงสุด<input type="number" min="0" placeholder="ทั้งหมด" value={maxPoint} onChange={e => setMaxPoint(e.target.value)}/></label>
      <label>จองคน PLAY เมื่อเล่นอย่างน้อย<input type="number" min="2" value={settings.playingAfterMinutes} onChange={e => setSettings(s => ({...s, playingAfterMinutes:e.target.value}))}/></label>
      <label>หมดเวลาจองภายใน<input type="number" value={settings.reservationMinutes} onChange={e => setSettings(s => ({...s, reservationMinutes:e.target.value}))}/></label>
      <button onClick={saveSettings} disabled={saving}>{saving ? 'กำลังบันทึก…' : 'บันทึกและคำนวณใหม่'}</button>
     </section>
     <section className="mp-rank-selector">
       <div><h2>🏸 เลือกระดับมือก่อนจัด Match</h2><p>เลือกได้สูงสุด 3 ประเภท · ถ้าไม่เลือกจะพิจารณาทุกระดับ</p></div>
       <div className="mp-rank-options">
         {PLANNER_RANKS.map(rank => {
           const players = data?.players || [];
           const active = players.filter(player => player.status === 'ACTIVE' && player.level_rank === rank && !player.queued_match_id).length;
           const play = players.filter(player => player.status === 'PLAY' && player.level_rank === rank && Number(player.playing_minutes || 0) >= Number(settings.playingAfterMinutes) && !player.queued_match_id).length;
           return <button type="button" key={rank} className={selectedRanks.includes(rank) ? 'is-selected' : ''} onClick={() => togglePlannerRank(rank)}><b>{rank}</b><span>พร้อม {active}</span><span>PLAY {play}</span></button>;
         })}
       </div>
       {selectedRanks.length > 0 && <button type="button" className="mp-rank-clear" onClick={() => setSelectedRanks([])}>ล้างที่เลือก</button>}
     </section>
     <div className="mp-summary"><button className="mp-waiting-open" onClick={()=>setWaitingPlayersOpen(true)}>⏱ ผู้เล่นรอนาน <b>{waitingPlayers.length}</b> คน</button><span className="mp-status-filter-label">เลือกผู้เล่น:</span><button type="button" className={statusFilter==='ACTIVE'?'is-active':''} onClick={()=>chooseStatusFilter('ACTIVE')}>เฉพาะ ACTIVE <b>{data?.players?.filter(p=>p.status==='ACTIVE').length || 0}</b></button><button type="button" className={!statusFilter?'is-active':''} onClick={()=>chooseStatusFilter('')}>ทั้งหมด <b>{data?.players?.length || 0}</b> คน</button><button type="button" className={statusFilter==='PLAY'?'is-active':''} onClick={()=>chooseStatusFilter('PLAY')}>เฉพาะ PLAY <b>{data?.players?.filter(p=>p.status==='PLAY'&&Number(p.playing_minutes||0)>=Number(data?.settings?.playing_after_minutes||2)&&!p.queued_match_id).length || 0}</b></button><span title="ผ่าน Rank, คะแนน, สถานะ Queue และเงื่อนไขการจัดทั้งหมด">พร้อมจัดจริง <b>{data?.eligible?.length || 0}</b> คน</span><span>คำแนะนำ <b>{data?.recommendations?.length || 0}</b> ชุด</span></div>
     <main className="mp-grid">
       <section className="mp-column mp-waiting-column">
         <div className="mp-title"><div><h2>⏱ คนรอนาน</h2><p>เรียงคนที่กำลังรอก่อน แล้วจึงแสดงผู้เล่นสถานะ PLAY</p></div></div>
         <div className="mp-waiting-primary-search">
           <PlayerSearchDropdown value={data?.priorityPlayer || null} allPlayers={data?.eligible || []} usedIds={[]} onChange={choosePrimaryPlayer}/>
           {primaryPlayerId && <button type="button" onClick={() => setPrimaryPlayerId('')}>ใช้คนรอนานอัตโนมัติ</button>}
         </div>
         <div className="mp-left-waiting-list">
           {eligibleWaitingPlayers.length === 0 ? <div className="mp-muted">ยังไม่มีผู้เล่นที่พร้อมพิจารณา</div> : eligibleWaitingPlayers.slice(0, 20).map((player, index) => (
             <button type="button" key={player.user_id} className={Number(data?.priorityPlayer?.user_id) === Number(player.user_id) ? 'is-primary' : ''} onClick={() => choosePrimaryPlayer(player)}>
               <b>{index + 1}</b><span><strong>{player.name}</strong><small>{player.level_rank} <PlayerIntensityBadge player={player}/></small></span><em>{player.status === 'PLAY' ? `PLAY ${Number(player.playing_minutes || 0)} นาที` : `รอ ${Number(player.wait_minutes || 0)} นาที`}</em>
             </button>
           ))}
         </div>
       </section>
       <section className="mp-column mp-wide"><div className="mp-title"><div><h2>✨ Match แนะนำ</h2><p>ตัดคู่ทีม/คู่ตรงข้ามซ้ำ 2 รอบแล้วเปลี่ยนคน · ACTIVE ไม่พอจึงดึง PLAY · Manual Edit สามารถ Approve ได้</p></div></div>
        {loading ? <div className="mp-empty">กำลังวิเคราะห์…</div> : !data?.recommendations?.length ? <div className="mp-empty"><b>ยังประกอบ Match 4 คนไม่ได้</b><span>ระบบจะตรวจใหม่แบบเรียลไทม์ หรือเพิ่มระยะคะแนนเพื่อเปิดโอกาสให้ลงสนาม</span></div> : <div className="mp-recommendations">{data.recommendations.map((item,index) => {
          const alternatives = localAlternatives(item, data?.waitingPairs || []);
          const variantIndex = Math.min(variantIndexes[item.id] || 0, alternatives.length - 1);
          const calculated = { ...item, ...alternatives[variantIndex] };
          const current = editedMatches[item.id] || calculated;
          const usedIds = [...current.team1, ...current.team2].map(player => player.user_id);
          return <article className={`mp-match ${current.relaxed?'is-warning':''}`} key={item.id}>
             <div className="mp-match-head"><span>Match Planner {index+1}</span><div><button className="mp-edit" onClick={()=>setEditingMatchId(editingMatchId===item.id?'':item.id)}>{editingMatchId===item.id?'✓ เสร็จ':'✏️ Edit ผู้เล่น'}</button><b className={current.status==='READY'?'ready':'reserved'}>{statusLabel(current.status)}</b></div></div>
             {item.planReason && <p className={`mp-plan-reason is-${String(item.planStage || '').toLowerCase()}`}>{item.planReason}</p>}
            <div className="mp-teams"><div><h3>Team 1 · {current.sum1}</h3>{current.team1.map((player,playerIndex)=>editingMatchId===item.id?<div className="mp-edit-player" key={player.user_id}>{protectedPairFor(player)?<div className="mp-fixed-player">{renderPlayer(player)}<small>🔒 แก้แยกไม่ได้</small></div>:<PlayerSearchDropdown value={player} allPlayers={editablePlayers} usedIds={usedIds.filter(id=>Number(id)!==Number(player.user_id))} onChange={replacement=>updateMatchPlayer(item.id,current,'team1',playerIndex,replacement)}/>}</div>:renderPlayer(player))}</div><strong>VS</strong><div><h3>Team 2 · {current.sum2}</h3>{current.team2.map((player,playerIndex)=>editingMatchId===item.id?<div className="mp-edit-player" key={player.user_id}>{protectedPairFor(player)?<div className="mp-fixed-player">{renderPlayer(player)}<small>🔒 แก้แยกไม่ได้</small></div>:<PlayerSearchDropdown value={player} allPlayers={editablePlayers} usedIds={usedIds.filter(id=>Number(id)!==Number(player.user_id))} onChange={replacement=>updateMatchPlayer(item.id,current,'team2',playerIndex,replacement)}/>}</div>:renderPlayer(player))}</div></div>
            <div className="mp-metrics"><span>ผู้เล่นห่าง <b>{current.playerGap}</b></span><span>ทีมต่าง <b>{current.teamGap}</b></span><span>Rank ห่าง <b>{current.rankGap}</b></span><span>คู่ซ้ำ <b>{current.repeatPartners}</b></span></div>
            <div className="mp-pair-details">
              <b>คู่ที่จัดมา</b>
              {current.historyChecking && <span className="is-checking">กำลังตรวจประวัติชุดใหม่…</span>}
              {(current.teammateDetails || []).map((pair,pairIndex)=><span className={pair.count>=2&&!pair.locked?'is-repeat':''} key={`team-${pairIndex}`}>{pair.locked?'🔒 ':'👥 '}{pair.names} · เคยคู่ {pair.count} รอบ</span>)}
              {(current.opponentDetails || []).filter(pair=>Number(pair.count)>0).map((pair,pairIndex)=><span className={pair.count>=2?'is-repeat':''} key={`opponent-${pairIndex}`}>⚔️ {pair.names} · เคยตรงข้าม {pair.count} รอบ</span>)}
            </div>
            {current.requiresApproval && <div className="mp-repeat-alert mp-repeat-alert-compact"><b>⚠️ ต้อง Approve ประวัติซ้ำ</b></div>}
            {current.relaxed && <p className="mp-warning">⚠️ ผ่อนปรนคะแนน/Rank เพื่อให้จัด Match ได้ โดย Waiting Pair และ Locked Pair ยังอยู่ทีมเดียวกันตลอด</p>}
            <div className="mp-match-actions">
              <button className="mp-swap" disabled={editingMatchId===item.id||current.historyChecking} onClick={() => { const nextIndex=(variantIndex+1)%alternatives.length;setVariantIndexes(previous=>({...previous,[item.id]:nextIndex}));recheckMatchHistory(item.id,{...item,...alternatives[nextIndex]}); }}>⇄ สลับคู่ {alternatives.length > 1 ? `(${variantIndex + 1}/${alternatives.length})` : ''}</button>
              {current.requiresApproval && <button className={`mp-approve ${approvedMatches[item.id]?'is-approved':''}`} disabled={Boolean(approvedMatches[item.id])} onClick={()=>{setApprovedMatches(previous=>({...previous,[item.id]:true}));setError('');}}>{approvedMatches[item.id]?'✓ Approve แล้ว':'🔓 Approve'}</button>}
              <button className="mp-queue" disabled={selected===item.id||current.historyChecking||(current.requiresApproval&&!approvedMatches[item.id])} onClick={()=>createQueue(current)}>{selected===item.id?'กำลังสร้าง…':current.historyChecking?'กำลังตรวจประวัติ…':current.requiresApproval&&!approvedMatches[item.id]?'รอ Approve':current.status==='READY'?'ส่งเข้า Match Queue':'จองเป็น Queue ล่วงหน้า'}</button>
            </div>
          </article>;
        })}</div>}
      </section>
      <aside className="mp-side">
        <section className="mp-card"><h2>💡 คู่ 2 คนแนะนำ</h2><p>เรียงจากคู่ล็อก เวลารอ และคะแนนใกล้กัน</p>{!data?.recommendedPairs?.length?<p className="mp-muted">ยังไม่มีคู่ที่เหมาะสม</p>:data.recommendedPairs.map(pair=><div className={`mp-suggested-pair ${pair.isLocked?'is-locked':''}`} key={pair.id}><div><b>{pair.players[0].name} + {pair.players[1].name}</b><span>{pair.players[0].level_rank}/{pair.players[1].level_rank} · ห่าง {pair.pointGap} คะแนน · {pair.status}</span>{pair.isLocked&&<em>🔒 คู่ Locked ต้องอยู่ด้วยกัน</em>}{pair.hasReservedMember&&<em>มีผู้เล่นติดคู่อื่นแล้ว</em>}</div><button disabled={pair.alreadyPaired||pair.hasReservedMember||pairSaving} onClick={()=>createPair(pair.players[0].user_id,pair.players[1].user_id)}>{pair.alreadyPaired?'ล็อกแล้ว':pair.hasReservedMember?'ติดคู่อื่น':'ล็อกคู่นี้'}</button></div>)}</section>
        <section className="mp-card" id="lock-pair"><h2>🔒 Lock Pair</h2><p>ค้นหาผู้เล่น 2 คนเพื่อล็อกให้อยู่คู่กัน</p><div className="mp-pair-search"><PlayerSearchDropdown value={pairA} allPlayers={selectable} usedIds={[pairB?.user_id].filter(Boolean)} onChange={setPairA}/><PlayerSearchDropdown value={pairB} allPlayers={selectable} usedIds={[pairA?.user_id].filter(Boolean)} onChange={setPairB}/></div><button disabled={!pairA||!pairB||pairSaving} onClick={()=>createPair()}>{pairSaving?'กำลังล็อก…':'+ Lock Pair'}</button></section>
        <section className="mp-card"><h2>🔒 Waiting Pairs</h2><p>รวม Waiting Pair และ Locked Pair จากหน้าจัด Match</p>{!data?.waitingPairs?.length?<p className="mp-muted">ยังไม่มีคู่ที่ล็อกไว้</p>:data.waitingPairs.map(pair=><div className={`mp-pair ${pair.pair_source==='LOCKED'?'is-locked':''}`} key={pair.id}><div><b>{pair.user_a_name} + {pair.user_b_name}</b><span>{pair.status} · ห่าง {pair.score_gap} คะแนน</span>{pair.pair_source==='LOCKED'&&<em>🔒 LOCKED PAIR · อยู่ด้วยกันตลอด</em>}</div>{pair.pair_source==='LOCKED'?<span className="mp-lock-badge">ล็อกถาวร</span>:<button title="ยกเลิก Waiting Pair" onClick={async()=>{await api(`/api/match-planner/${groupId}/pairs/${pair.id}`,{method:'DELETE'});load(true);}}>×</button>}</div>)}</section>
        <section className="mp-card"><h2>📨 คำขอจากผู้เล่น</h2><p>ตรวจรายชื่อก่อน Approve เพื่อส่งคู่เข้า Waiting Pair หรือ Match เข้า Queue</p>{!data?.requests?.length?<p className="mp-muted">ยังไม่มีคำขอที่เปิดอยู่</p>:data.requests.map(request=><div className="mp-player-request" key={request.id}><header><b>{request.requester_name}</b><span>{request.request_mode} คน</span></header><div>{(request.players||[]).map((player,index)=><span key={player.user_id}>{index+1}. {player.name} · {player.level_rank} · {player.status}</span>)}</div>{request.note&&<p>หมายเหตุ: {request.note}</p>}<footer><button disabled={Boolean(requestBusy)} onClick={()=>decideRequest(request,'REJECT')}>ยกเลิก</button><button disabled={Boolean(requestBusy)} onClick={()=>decideRequest(request,'APPROVE')}>{requestBusy===`${request.id}-APPROVE`?'กำลัง Approve…':'✓ Approve'}</button></footer></div>)}</section>
      </aside>
    </main>
    {controlOpen && <div className="mp-control-backdrop" role="presentation" onMouseDown={event=>{if(event.target===event.currentTarget)setControlOpen(false);}}>
      <section className="mp-control-modal" role="dialog" aria-modal="true" aria-label="Queue และ Match Play">
        <header><div><h2>🏸 Queue & Match Play</h2><p>เริ่มสนามและจบผลการแข่งขันได้จากหน้านี้</p></div><button onClick={()=>setControlOpen(false)}>×</button></header>
        <div className="mp-control-tabs-summary"><span>Queue <b>{controlData.queue.length}</b></span><span>กำลังเล่น <b>{controlData.playing.length}</b></span><button onClick={()=>loadControlData()}>↻ Refresh</button></div>
        {controlLoading ? <div className="mp-control-empty">กำลังโหลด…</div> : <div className="mp-control-columns">
          <div><h3>⏳ Match Queue</h3>{!controlData.queue.length?<div className="mp-control-empty">ไม่มี Match ใน Queue</div>:controlData.queue.map(match=>{const [team1,team2]=compactPlayers(match);return <article className="mp-control-match" key={`q-${match.matchId}`}><div className="mp-control-match-head"><b>Match #{match.groupMatchNo || match.matchId}</b><span>รอเริ่ม</span></div><div className="mp-control-teams"><span><i>T1</i>{team1.map(player=>player.name).join(' • ')}</span><em>VS</em><span><i>T2</i>{team2.map(player=>player.name).join(' • ')}</span></div><button className="mp-control-start" disabled={Boolean(controlBusy)} onClick={()=>startControlMatch(match)}>{controlBusy===`start-${match.matchId}`?'กำลังเริ่ม…':'▶ Start Match'}</button></article>})}</div>
          <div><h3>⚡ Match Play</h3>{!controlData.playing.length?<div className="mp-control-empty">ยังไม่มี Match กำลังเล่น</div>:controlData.playing.map(match=>{const [team1,team2]=compactPlayers(match);return <article className="mp-control-match is-playing" key={`p-${match.matchId}`}><div className="mp-control-match-head"><b>Match #{match.groupMatchNo || match.matchId}</b><span>Court {match.court_no || '-'}</span></div><div className="mp-control-teams"><span><i>T1</i>{team1.map(player=>player.name).join(' • ')}</span><em>VS</em><span><i>T2</i>{team2.map(player=>player.name).join(' • ')}</span></div><div className="mp-control-results"><button disabled={Boolean(controlBusy)} onClick={()=>endControlMatch(match,'TEAM1')}>T1 Win</button><button disabled={Boolean(controlBusy)} onClick={()=>endControlMatch(match,'DRAW')}>Draw</button><button disabled={Boolean(controlBusy)} onClick={()=>endControlMatch(match,'TEAM2')}>T2 Win</button></div></article>})}</div>
        </div>}
      </section>
    </div>}
    {waitingPlayersOpen&&<aside className={`rmp-waiting-alert-panel mp-dashboard-alert ${waitingAlertMode==='LOW_GAMES'?'is-low-games':''}`} role="dialog" aria-label="แจ้งเตือนผู้เล่น" style={waitingAlertPosition?{left:waitingAlertPosition.x,top:waitingAlertPosition.y,right:'auto'}:undefined} onPointerDown={event=>{if(event.target.closest('button')||!event.target.closest('.mp-dashboard-alert-head'))return;const rect=event.currentTarget.getBoundingClientRect();waitingAlertDragRef.current={pointerX:event.clientX,pointerY:event.clientY,left:rect.left,top:rect.top,width:rect.width,height:rect.height};event.currentTarget.setPointerCapture?.(event.pointerId);}} onPointerMove={moveWaitingAlert} onPointerUp={finishWaitingAlertDrag} onPointerCancel={finishWaitingAlertDrag}>
      <header className="mp-dashboard-alert-head"><div><b>{waitingAlertMode==='LOW_GAMES'?'⚠️ ผู้เล่นที่ได้ลงเกมน้อย':'⚠️ ผู้เล่นที่รอนาน'}</b><small>ลากบริเวณหัวกล่องเพื่อย้ายตำแหน่ง</small></div><button type="button" onClick={()=>setWaitingPlayersOpen(false)}>×</button></header>
      <nav><button type="button" className={waitingAlertMode==='WAITING'?'is-active is-waiting':''} onClick={()=>{setWaitingAlertMode('WAITING');setWaitingAlertPage(0);}}>รอนาน ({waitingActivePlayers.length})</button><button type="button" className={waitingAlertMode==='LOW_GAMES'?'is-active is-low':''} onClick={()=>{setWaitingAlertMode('LOW_GAMES');setWaitingAlertPage(0);}}>เกมน้อย ({lowGamePlayers.length})</button>{[5,10,20].map(limit=><button type="button" key={limit} className={waitingAlertLimit===limit?'is-active is-limit':''} onClick={()=>{setWaitingAlertLimit(limit);setWaitingAlertPage(0);}}>Top {limit}</button>)}</nav>
      <ol>{!visibleWaitingAlertPlayers.length?<li className="is-empty">ยังไม่มีผู้เล่นในรายการ</li>:visibleWaitingAlertPlayers.map((player,index)=><li key={player.user_id}><strong>{waitingAlertPage*waitingAlertLimit+index+1}.</strong><span><b>{player.name}</b> Rank {player.level_rank}<em>{waitingAlertMode==='LOW_GAMES'?`● จบแล้ว ${Number(player.total_games||0)} เกม`:`● รอ ${Number(player.wait_minutes||0)} นาที`}</em></span><button type="button" disabled={holdBusy===`${player.user_id}-HOLD`} onClick={()=>updateHoldStatus(player,'HOLD')}>{holdBusy===`${player.user_id}-HOLD`?'กำลังบันทึก…':'⏸ HOLD'}</button></li>)}</ol>
      {waitingAlertPageCount>1&&<footer><button type="button" disabled={waitingAlertPage===0} onClick={()=>setWaitingAlertPage(page=>Math.max(0,page-1))}>← ก่อนหน้า</button><b>หน้า {waitingAlertPage+1}/{waitingAlertPageCount}</b><button type="button" disabled={waitingAlertPage>=waitingAlertPageCount-1} onClick={()=>setWaitingAlertPage(page=>Math.min(waitingAlertPageCount-1,page+1))}>ถัดไป →</button></footer>}
    </aside>}
    {holdOpen && <div className="mp-control-backdrop" role="presentation" onMouseDown={event=>{if(event.target===event.currentTarget)setHoldOpen(false);}}>
      <section className="mp-action-modal" role="dialog" aria-modal="true" aria-label="จัดการ HOLD">
        <header><div><h2>⏸ จัดการ HOLD</h2><p>พักผู้เล่นหรือเรียกกลับเป็น ACTIVE</p></div><button onClick={()=>setHoldOpen(false)}>×</button></header>
        <div className="mp-action-body"><input type="search" placeholder="ค้นหาชื่อผู้เล่น" value={holdSearch} onChange={event=>setHoldSearch(event.target.value)}/><div className="mp-hold-list">{(data?.players||[]).filter(player=>String(player.name||'').toLowerCase().includes(holdSearch.trim().toLowerCase())).map(player=>{const nextStatus=player.status==='HOLD'?'ACTIVE':'HOLD';return <article key={player.user_id}><div><b>{player.name}</b><span>{player.level_rank} · {player.status} <PlayerIntensityBadge player={player}/></span></div><button disabled={Boolean(holdBusy)||!['ACTIVE','HOLD'].includes(player.status)} onClick={()=>updateHoldStatus(player,nextStatus)}>{holdBusy===`${player.user_id}-${nextStatus}`?'กำลังบันทึก…':player.status==='HOLD'?'เรียกกลับ ACTIVE':'พัก HOLD'}</button></article>})}</div></div>
      </section>
    </div>}
    {manualOpen && <div className="mp-control-backdrop" role="presentation" onMouseDown={event=>{if(event.target===event.currentTarget)setManualOpen(false);}}>
      <section className="mp-action-modal mp-manual-modal mp-manual-minimal" role="dialog" aria-modal="true" aria-label="เลือกผู้เล่น 4 คน">
        <header><div><h2>เลือกผู้เล่น 4 คน</h2><p>เลือกคนแรก แล้วระบบจะเรียงระดับมือและคะแนนที่ใกล้เคียงให้</p></div><button onClick={()=>setManualOpen(false)}>×</button></header>
        <div className="mp-action-body">
          <div className="mp-manual-slots">{manualPlayers.map((player,index)=><button type="button" className={player?'is-filled':''} key={index} onClick={()=>player&&toggleManualPlayer(player)}>{player?<><img src={player.avatar_url||'/namad-logo.png'} alt=""/><span><b>{player.name}</b><small>{index<2?'ทีม 1':'ทีม 2'} · {player.level_rank} <PlayerIntensityBadge player={player}/></small></span><i>×</i></>:<><em>{index+1}</em><span><b>เลือกผู้เล่น</b><small>{index<2?'ทีม 1':'ทีม 2'}</small></span></>}</button>)}</div>
          <input className="mp-manual-search" type="search" value={manualSearch} onChange={event=>setManualSearch(event.target.value)} placeholder="ค้นหาชื่อผู้เล่น" />
          <div className="mp-manual-order" role="group" aria-label="เรียงรายชื่อผู้เล่น"><button type="button" className={manualOrder==='NEARBY'?'is-active':''} onClick={()=>setManualOrder('NEARBY')}>🎯 ระดับใกล้เคียง</button><button type="button" className={manualOrder==='WAIT_LONG'?'is-active':''} onClick={()=>setManualOrder('WAIT_LONG')}>⏳ รอนานที่สุด</button><button type="button" className={manualOrder==='LOW_GAMES'?'is-active':''} onClick={()=>setManualOrder('LOW_GAMES')}>🏸 ลงเล่นน้อยที่สุด</button></div>
          {manualPlayers.filter(Boolean).length===4&&<section className="mp-four-relation-check"><header><div><b>ตรวจประวัติผู้เล่นทั้ง 4 คน</b><small>ตรวจครบ 6 ความสัมพันธ์ในก๊วนนี้</small></div>{manualRelationCheck.loading?<span className="is-loading">กำลังตรวจ…</span>:manualRelationRows.some(item=>Number(item.count||0)>=2)?<span className="is-danger">⚠️ พบคู่ซ้ำ</span>:<span className="is-clear">✅ ตรวจแล้ว</span>}</header>{manualRelationCheck.error?<p className="mp-history-error">{manualRelationCheck.error}</p>:<div>{manualRelationRows.map((item,index)=>{const count=Number(item.count||0);return <span className={count>=2?'is-danger':count===1?'is-seen':'is-clear'} key={`${item.relation}-${item.names}-${index}`}><i>{item.relation==='TEAMMATE'?'👥':'⚔️'}</i><b>{item.names}</b><small>{count===0?'ยังไม่เคยพบกัน':`${item.relation==='TEAMMATE'?'คู่กัน':'ตรงข้ามกัน'} ${count} รอบ`}</small><em>{count>=2?'⚠ ซ้ำ':count===1?'เคยพบ':'✓ ใหม่'}</em></span>})}</div>}</section>}
          <div className="mp-manual-layout"><div className="mp-manual-candidates">{partnerCheckAnchor&&<div className="mp-partner-check-hint">{manualPartnerCheck.loading?'กำลังตรวจประวัติคู่ทีม…':<>ตรวจคนที่จะคู่กับ <b>{partnerCheckAnchor.name}</b> · สีแดงคือเคยคู่กันแล้ว</>}</div>}{manualCandidates.map(player=>{const partnerRelation=manualPartnerCheck.anchorUserId===Number(partnerCheckAnchor?.user_id)?manualPartnerCheck.relations[Number(player.user_id)]:null;const teammateCount=Number(partnerRelation?.teammateCount||0);return <button type="button" key={player.user_id} className={`${manualSelectedIds.has(Number(player.user_id))?'is-selected':''} ${teammateCount>0?'is-repeat-partner':''}`} onClick={()=>toggleManualPlayer(player)}><img src={player.avatar_url||'/namad-logo.png'} alt=""/><span><b>{player.name}</b><small>ระดับมือ {player.level_rank} <PlayerIntensityBadge player={player}/></small><em>{player.status==='ACTIVE'?'พร้อมเล่น':player.status} · รอ {Number(player.wait_minutes||0)} นาที · เล่นแล้ว {Number(player.total_games||0)} เกม</em></span>{teammateCount>0?<i className="is-repeat-badge">👥 คู่กันแล้ว {teammateCount} รอบ</i>:manualAnchor&&Number(player.user_id)!==Number(manualAnchor.user_id)&&<i>ห่าง {rankDistance(player.level_rank,manualAnchor.level_rank)} ระดับ</i>}</button>})}</div>
          <div className="mp-manual-history"><h3>ประวัติในก๊วนนี้</h3>{manualPlayers.filter(Boolean).length===0?<p>เลือกผู้เล่นเพื่อดูประวัติ</p>:manualPlayers.filter(Boolean).map(player=>{const history=manualHistory[player.user_id];return <article key={player.user_id}><header><img src={player.avatar_url||'/namad-logo.png'} alt=""/><div><b>{player.name}</b><small>{Number(player.total_games||0)} เกมทั้งหมด</small></div></header>{history?.loading?<p>กำลังโหลด…</p>:history?.error?<p className="mp-history-error">โหลดประวัติไม่สำเร็จ: {history.error}</p>:!history?.matches?.length?<p>ยังไม่มีแมทที่กำลังเล่นหรือเล่นจบ</p>:<div>{history.matches.slice(0,8).map(match=><span key={match.match_id} className={`is-${String(match.outcome).toLowerCase()}`}><b>#{match.group_match_no||match.match_id}</b><em>{match.outcome==='PLAYING'?'กำลังเล่น':match.outcome==='WIN'?'ชนะ':match.outcome==='DRAW'?'เสมอ':'แพ้'}</em><small>คู่ {match.teammates||'-'} · พบ {match.opponents||'-'}</small></span>)}</div>}</article>})}</div></div>
          <footer><button disabled={Boolean(manualBusy)||manualPlayers.filter(Boolean).length!==4} onClick={()=>createManualMatch(false)}>{manualBusy==='queue'?'กำลังส่ง…':'เข้าคิวรอสนาม'}</button><button disabled={Boolean(manualBusy)||manualPlayers.filter(Boolean).length!==4} onClick={()=>createManualMatch(true)}>{manualBusy==='start'?'กำลังสร้าง…':'สร้างและเริ่มเกม'}</button></footer>
        </div>
      </section>
    </div>}
  </div>;
}

export default MatchPlanner;
